import {
  IonButton,
  IonContent,
  IonImg,
  IonIcon,
  IonInput,
  IonMenuToggle,
  IonModal,
  IonPage,
  useIonToast,
} from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import { AuthService, ILookupCasinoAccountResponse } from 'services/auth';
import {
  DELETE_CASINO_ACCOUNTS_GROUP,
  canAccessDeleteCasinoAccounts,
  emailsMatchForDeleteConfirm,
} from './deleteCasinoAccountsAccess';

type LookedUpAccount = NonNullable<ILookupCasinoAccountResponse['account']>;

const MenuKebab = () => (
  <div className="player-engagement__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="player-engagement__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="player-engagement__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const MenuBack = () => (
  <IonMenuToggle menu="main">
    <IonButton fill="clear" className="player-engagement__back" aria-label="Open menu">
      <IonImg src="assets/images/back-icon.svg" />
    </IonButton>
  </IonMenuToggle>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent player-engagement delete-casino-accounts">
    <IonContent className="player-engagement delete-casino-accounts">
      <MenuKebab />
      <div className="player-engagement__content">
        <div className="player-engagement__breadcrumb-row">
          <MenuBack />
          <div className="player-engagement__breadcrumb">Delete Casino Accounts</div>
        </div>
        <div className="player-engagement__forbidden-card">
          <h2>403 · Delete Casino Accounts not enabled</h2>
          <p>
            Requires Cognito group <strong>{DELETE_CASINO_ACCOUNTS_GROUP}</strong>. Ask ops to
            assign it, then sign out and sign back in.
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

function httpStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { status?: number }; status?: number };
  return e.response?.status ?? e.status;
}

function serverMessage(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as {
    response?: { data?: { response?: { message?: string }; message?: string } };
    message?: string;
  };
  return (
    e.response?.data?.response?.message ||
    e.response?.data?.message ||
    (typeof e.message === 'string' ? e.message : undefined)
  );
}

function formatCreatedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')}`;
  } catch {
    return iso;
  }
}

export const DeleteCasinoAccountsPage = () => {
  const [presentToast] = useIonToast();
  const authService = useMemo(() => new AuthService(), []);
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = canAccessDeleteCasinoAccounts(groups);

  const [email, setEmail] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [account, setAccount] = useState<LookedUpAccount | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const emailInputRef = useRef<HTMLIonInputElement | null>(null);

  const confirmEnabled = Boolean(
    account && emailsMatchForDeleteConfirm(account.email, confirmEmail)
  );

  const resetForm = useCallback(() => {
    setEmail('');
    setAccount(null);
    setConfirmEmail('');
    setLookupMessage(null);
    setShowConfirmDialog(false);
  }, []);

  const runLookup = useCallback(async () => {
    let live = email;
    try {
      const native = await emailInputRef.current?.getInputElement();
      if (native && typeof native.value === 'string') {
        live = native.value;
      }
    } catch {
      /* keep state */
    }
    const trimmed = live.trim();
    setEmail(trimmed);
    setAccount(null);
    setConfirmEmail('');
    setLookupMessage(null);
    if (!trimmed || !canAccess) return;

    setLookingUp(true);
    try {
      const res = await firstValueFrom(authService.LookupCasinoAccount(trimmed));
      const payload = res?.response;
      if (payload?.found && payload.account) {
        setAccount(payload.account);
        setLookupMessage(null);
      } else {
        setAccount(null);
        setLookupMessage(
          payload?.message || 'No casino account with that email in your casinos'
        );
      }
    } catch (err: unknown) {
      setAccount(null);
      const status = httpStatus(err);
      void presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message:
          status === 403
            ? 'Delete Casino Accounts is not enabled for this account.'
            : status === 401
              ? 'Please sign in again.'
              : serverMessage(err) || 'Lookup failed. Please try again.',
      });
    } finally {
      setLookingUp(false);
    }
  }, [authService, canAccess, email, presentToast]);

  const runDelete = useCallback(async () => {
    if (!account || !confirmEnabled || deleting) return;
    setDeleting(true);
    try {
      const res = await firstValueFrom(
        authService.DeleteCasinoAccount(account.email, confirmEmail.trim())
      );
      const payload = res?.response;
      if (payload?.status === 'deleted') {
        void presentToast({
          position: 'top',
          duration: 4500,
          color: 'success',
          message: payload.message || `Deleted ${account.email}`,
        });
        resetForm();
      } else if (payload?.status === 'not_found') {
        void presentToast({
          position: 'top',
          duration: 4000,
          color: 'warning',
          message: payload.message || 'No casino account with that email in your casinos',
        });
        resetForm();
      } else {
        void presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message: payload?.message || 'Delete did not complete. You can try again.',
        });
      }
      setShowConfirmDialog(false);
    } catch (err: unknown) {
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: serverMessage(err) || 'Delete failed. Please try again.',
      });
      setShowConfirmDialog(false);
    } finally {
      setDeleting(false);
    }
  }, [
    account,
    authService,
    confirmEmail,
    confirmEnabled,
    deleting,
    presentToast,
    resetForm,
  ]);

  if (!canAccess) {
    return <ForbiddenCard />;
  }

  const casinoLabel =
    account?.casinoNames?.join(', ') ||
    account?.casinos?.map((c) => c.casinoName).join(', ') ||
    '—';

  return (
    <IonPage className="app-parent player-engagement delete-casino-accounts">
      <IonContent className="player-engagement delete-casino-accounts">
        <MenuKebab />
        <div className="player-engagement__content">
          <div className="player-engagement__breadcrumb-row">
            <MenuBack />
            <div className="player-engagement__breadcrumb">
              <span>Delete Casino Accounts</span>
            </div>
          </div>

          <div className="player-engagement__header">
            <div className="player-engagement__title-row">
              <h1 className="player-engagement__title">Delete Casino Accounts</h1>
            </div>
            <p className="delete-casino-accounts__subtitle">
              Look up one casino account by email, confirm, then permanently delete it from
              SlotCheck.
            </p>
          </div>

          <form
            className="delete-casino-accounts__lookup"
            onSubmit={(e) => {
              e.preventDefault();
              void runLookup();
            }}
          >
            <IonInput
              ref={emailInputRef}
              className="delete-casino-accounts__input"
              type="email"
              value={email}
              placeholder="Casino account email"
              onIonInput={(e) => {
                const raw = (e.target as HTMLIonInputElement | null)?.value;
                setEmail(String(raw ?? ''));
              }}
              onIonChange={(e) => setEmail(String(e.detail.value ?? ''))}
              aria-label="Casino account email"
            />
            <IonButton
              type="submit"
              className="delete-casino-accounts__lookup-btn"
              disabled={lookingUp || !email.trim()}
            >
              {lookingUp ? 'Looking up…' : 'Look up'}
            </IonButton>
          </form>

          {lookupMessage && (
            <p className="delete-casino-accounts__empty" role="status">
              {lookupMessage}
            </p>
          )}

          {account && (
            <div className="delete-casino-accounts__card" data-testid="delete-casino-account-card">
              <div className="delete-casino-accounts__card-row">
                <span className="delete-casino-accounts__label">Email</span>
                <span className="delete-casino-accounts__value">{account.email}</span>
              </div>
              <div className="delete-casino-accounts__card-row">
                <span className="delete-casino-accounts__label">Casinos</span>
                <span className="delete-casino-accounts__value">{casinoLabel}</span>
              </div>
              <div className="delete-casino-accounts__card-row">
                <span className="delete-casino-accounts__label">Created</span>
                <span className="delete-casino-accounts__value">
                  {formatCreatedAt(account.createdAt)}
                </span>
              </div>

              <label className="delete-casino-accounts__confirm-label" htmlFor="dca-confirm-email">
                Re-type email to confirm
              </label>
              <IonInput
                id="dca-confirm-email"
                className="delete-casino-accounts__input"
                type="email"
                value={confirmEmail}
                placeholder={account.email}
                onIonInput={(e) => {
                  const raw = (e.target as HTMLIonInputElement | null)?.value;
                  setConfirmEmail(String(raw ?? ''));
                }}
                onIonChange={(e) => setConfirmEmail(String(e.detail.value ?? ''))}
                aria-label="Re-type email to confirm"
              />

              <IonButton
                className="delete-casino-accounts__delete-btn"
                disabled={!confirmEnabled || deleting}
                onClick={() => setShowConfirmDialog(true)}
              >
                Delete account
              </IonButton>
            </div>
          )}
        </div>

        <IonModal
          isOpen={showConfirmDialog}
          onDidDismiss={() => {
            if (!deleting) setShowConfirmDialog(false);
          }}
          className="delete-casino-accounts__modal"
        >
          <div className="delete-casino-accounts__dialog">
            <h2>Confirm delete</h2>
            <p>
              This will permanently delete <strong>{account?.email}</strong> from SlotCheck. This
              cannot be undone.
            </p>
            <p className="delete-casino-accounts__dialog-casinos">
              Casinos: <strong>{casinoLabel}</strong>
            </p>
            <div className="delete-casino-accounts__dialog-actions">
              <IonButton
                fill="outline"
                className="delete-casino-accounts__cancel-btn"
                disabled={deleting}
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </IonButton>
              <IonButton
                className="delete-casino-accounts__danger-btn"
                disabled={deleting}
                onClick={() => void runDelete()}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </IonButton>
            </div>
          </div>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default DeleteCasinoAccountsPage;
